import React, { useState, useCallback, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import api from '@app/services/api';
import { useMentionSearch } from '@platform/query';
import { formatDate as formatGlobalDate } from '@platform/app-shell/frontend/utils/dateFormat';
import AttachmentPreview, { previewKind } from '@modules/database/work-items/frontend/components/AttachmentPreview';
import { handleIssueLinkActivation } from '@modules/zebra-search/frontend/utils/issueLinks';
import { useEditor, EditorContent, ReactNodeViewRenderer, NodeViewWrapper, ReactRenderer, Extension, Mark } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Underline from '@tiptap/extension-underline';
import Image from '@tiptap/extension-image';
import Mention from '@tiptap/extension-mention';
import { Table } from '@tiptap/extension-table';
import { TableRow } from '@tiptap/extension-table-row';
import { TableCell } from '@tiptap/extension-table-cell';
import { TableHeader } from '@tiptap/extension-table-header';
import { CellSelection, TableMap } from 'prosemirror-tables';
import tippy from 'tippy.js';

// Mark: ADF textColor — renders colored text from Jira descriptions
const TextColor = Mark.create({
  name: 'textColor',
  addAttributes() {
    return { color: { default: null } };
  },
  parseHTML() {
    return [{ tag: 'span[data-text-color]', getAttrs: el => ({ color: el.getAttribute('data-text-color') }) }];
  },
  renderHTML({ HTMLAttributes }) {
    return ['span', { style: `color: ${HTMLAttributes.color}`, 'data-text-color': HTMLAttributes.color }, 0];
  },
});

// Extension: Backspace/Delete on table cell selections deletes rows/columns
const TableDeleteKeymap = Extension.create({
  name: 'tableDeleteKeymap',

  addKeyboardShortcuts() {
    const handleDelete = () => {
      const { editor } = this;
      const { selection } = editor.state;

      if (!(selection instanceof CellSelection)) return false;

      // Get the table node and map
      const $anchor = selection.$anchorCell;
      const table = $anchor.node(-1);
      const tableMap = TableMap.get(table);
      const totalRows = tableMap.height;
      const totalCols = tableMap.width;

      // Collect selected cell positions
      const selectedCells = [];
      selection.forEachCell((_node, pos) => {
        selectedCells.push(pos);
      });

      // Map selected cells to row/col indices
      const tableStart = $anchor.start(-1);
      const selectedIndices = selectedCells.map(pos => {
        const cellOffset = pos - tableStart;
        const idx = tableMap.map.indexOf(cellOffset);
        if (idx === -1) return null;
        return { row: Math.floor(idx / totalCols), col: idx % totalCols };
      }).filter(Boolean);

      if (selectedIndices.length === 0) return false;

      const selectedRows = new Set(selectedIndices.map(i => i.row));
      const selectedCols = new Set(selectedIndices.map(i => i.col));

      // Check if full row(s) selected: all columns covered for the selected rows
      const isFullRows = [...selectedRows].every(row => {
        const colsInRow = selectedIndices.filter(i => i.row === row).map(i => i.col);
        return colsInRow.length === totalCols;
      });

      // Check if full column(s) selected: all rows covered for the selected columns
      const isFullCols = [...selectedCols].every(col => {
        const rowsInCol = selectedIndices.filter(i => i.col === col).map(i => i.row);
        return rowsInCol.length === totalRows;
      });

      // Don't delete if it would remove the entire table (all rows or all cols)
      if (isFullRows && selectedRows.size >= totalRows) {
        editor.chain().focus().deleteTable().run();
        return true;
      }
      if (isFullCols && selectedCols.size >= totalCols) {
        editor.chain().focus().deleteTable().run();
        return true;
      }

      if (isFullRows) {
        // Delete selected rows (from bottom to top to preserve indices)
        const rowCount = selectedRows.size;
        for (let i = 0; i < rowCount; i++) {
          editor.chain().focus().deleteRow().run();
        }
        return true;
      }

      if (isFullCols) {
        // Delete selected columns (from right to left to preserve indices)
        const colCount = selectedCols.size;
        for (let i = 0; i < colCount; i++) {
          editor.chain().focus().deleteColumn().run();
        }
        return true;
      }

      // Partial selection: clear cell contents
      const { tr } = editor.state;
      selection.forEachCell((cell, pos) => {
        const start = pos + 1;
        const end = pos + cell.nodeSize - 1;
        if (end > start) {
          tr.replaceWith(start, end, editor.state.schema.nodes.paragraph.create());
        }
      });
      editor.view.dispatch(tr);
      return true;
    };

    return {
      Backspace: handleDelete,
      Delete: handleDelete,
    };
  },
});
import SmartLink from '@modules/database/work-items/frontend/editors/smartLink/SmartLink';
import { attachmentInsertRange } from '@modules/database/work-items/frontend/utils/attachmentInsert';
import '@modules/database/work-items/frontend/editors/RichTextEditor.css';

// Format ISO date for the attachment label — respects the PWA-global
// date format setting.
function formatAttachmentDate(iso) {
  return iso ? formatGlobalDate(iso, 'datetime') : null;
}

// Build an authenticated image URL by appending ?token= to our proxy URLs
function getAuthenticatedSrc(src) {
  if (!src) return src;
  const token = localStorage.getItem('token');
  if (!token) return src;
  // Only append token to our own proxy URLs
  if (src.includes('/api/attachment-content/') || src.includes('/api/media-content/')) {
    const separator = src.includes('?') ? '&' : '?';
    return `${src}${separator}token=${encodeURIComponent(token)}`;
  }
  return src;
}

// The same URL, asking the server to answer with an attachment disposition
// (?download=1) so the browser saves the file under its own name instead of
// previewing it. Only our own proxy URLs know the flag.
function getDownloadSrc(authSrc) {
  if (!authSrc) return authSrc;
  if (authSrc.includes('/api/attachment-content/') || authSrc.includes('/api/media-content/')) {
    return `${authSrc}${authSrc.includes('?') ? '&' : '?'}download=1`;
  }
  return authSrc;
}

// The ⬇ chip on inline media — the same one the Attachments section
// carries, so a file in a wiki field saves without scrolling to the panel.
// Sits inside a contentEditable=false island so the editor never treats the
// click as a caret move or a node selection.
function MediaDownloadChip({ authSrc, filename }) {
  const href = getDownloadSrc(authSrc);
  if (!href) return null;
  return (
    <a
      className="rte-media-download"
      href={href}
      download={filename}
      title={`Download ${filename}`}
      aria-label={`Download ${filename}`}
      contentEditable={false}
      draggable={false}
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M12 3v12" /><polyline points="6 11 12 17 18 11" /><path d="M4 21h16" />
      </svg>
    </a>
  );
}

// File type icon: returns an SVG based on the file extension
function getFileTypeIcon(filename, size = 32) {
  // No dot → no extension. Without this, a label like 'Jira attachment'
  // would masquerade as extension "jira attachment" and badge as "JIRA".
  const ext = (filename || '').includes('.') ? filename.split('.').pop().toLowerCase() : '';
  const h = Math.round(size * 1.25);
  let color, label;
  switch (ext) {
    case 'xls': case 'xlsx': case 'xlsm': case 'csv':
      color = '#1D6F42'; label = 'XLS'; break;
    case 'doc': case 'docx':
      color = '#2B579A'; label = 'DOC'; break;
    case 'pdf':
      color = '#DC3545'; label = 'PDF'; break;
    case 'ppt': case 'pptx':
      color = '#D24726'; label = 'PPT'; break;
    case 'zip': case 'rar': case '7z': case 'tar': case 'gz':
      color = '#8B6914'; label = 'ZIP'; break;
    case 'txt': case 'log': case 'md': case 'rtf':
      color = '#6C757D'; label = 'TXT'; break;
    case 'dwg': case 'dxf':
      color = '#0696D7'; label = 'CAD'; break;
    case 'stp': case 'step': case 'igs': case 'iges': case 'prt': case 'stl':
      color = '#00897B'; label = '3D'; break;
    case 'mp4': case 'avi': case 'mov': case 'wmv': case 'mkv':
      color = '#7B1FA2'; label = 'VID'; break;
    case 'mp3': case 'wav': case 'flac': case 'aac':
      color = '#E91E63'; label = 'AUD'; break;
    case 'png': case 'jpg': case 'jpeg': case 'gif': case 'svg': case 'webp': case 'bmp':
      color = '#0288D1'; label = 'IMG'; break;
    default:
      color = '#6C757D'; label = ext ? ext.toUpperCase().slice(0, 4) : 'FILE'; break;
  }
  return (
    <svg width={size} height={h} viewBox="0 0 32 40" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M2 4C2 1.79 3.79 0 6 0H20L30 10V36C30 38.21 28.21 40 26 40H6C3.79 40 2 38.21 2 36V4Z" fill="#F8F9FA" stroke="#DEE2E6" strokeWidth="1"/>
      <path d="M20 0L30 10H24C21.79 10 20 8.21 20 6V0Z" fill="#DEE2E6"/>
      <rect x="2" y="23" width="28" height="13" fill={color} opacity="0.9"/>
      <text x="16" y="33.5" textAnchor="middle" fill="white" fontSize="8" fontFamily="system-ui, -apple-system, sans-serif" fontWeight="700">{label}</text>
    </svg>
  );
}

// Custom node view: renders as attachment card in readonly, resizable full-size in edit
function AttachmentCardView({ node, editor, selected, updateAttributes }) {
  const { src, alt, uploadDate, width } = node.attrs;
  const filename = alt || 'Attachment';
  const [imgError, setImgError] = useState(false);
  const [resizing, setResizing] = useState(false);
  // A plain click on a read-only image / file card opens the same preview
  // the Attachments section uses, in this window; middle/Ctrl-click keeps
  // the href and goes to a new tab.
  const [previewOpen, setPreviewOpen] = useState(false);
  const imgRef = useRef(null);
  const startXRef = useRef(0);
  const startWidthRef = useRef(0);

  // Media refs that resolveImageRefs couldn't (or didn't get to) resolve
  // arrive here still carrying their jira-media:// / wiki-image:// scheme.
  // Stride-uploaded files store the NUMERIC Stride attachment id as the ADF
  // media id, so those can always be served from /api/attachment-content/:id
  // — the same endpoint (and same ?token= auth via getAuthenticatedSrc) the
  // Attachments panel uses. Only Jira-era UUID media with no matching
  // attachment stays unresolved. Display-only: node.attrs are not mutated,
  // so the stored ADF round-trips unchanged.
  const jiraMediaId = src?.startsWith('jira-media://') ? src.slice('jira-media://'.length) : null;
  const strideAttachmentSrc = jiraMediaId && /^\d+$/.test(jiraMediaId)
    ? api.getAttachmentContentUrl(jiraMediaId)
    : null;
  const isUnresolved = !strideAttachmentSrc
    && (src?.startsWith('jira-media://') || src?.startsWith('wiki-image://'));
  const authSrc = isUnresolved ? null : getAuthenticatedSrc(strideAttachmentSrc || src);

  // Resize handlers
  const onResizeStart = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    const clientX = e.type.startsWith('touch') ? e.touches[0].clientX : e.clientX;
    startXRef.current = clientX;
    startWidthRef.current = imgRef.current?.offsetWidth || 300;
    setResizing(true);

    const onMove = (ev) => {
      const cx = ev.type.startsWith('touch') ? ev.touches[0].clientX : ev.clientX;
      const delta = cx - startXRef.current;
      const newWidth = Math.max(80, startWidthRef.current + delta);
      if (imgRef.current) {
        imgRef.current.style.width = `${newWidth}px`;
      }
    };

    const onEnd = (ev) => {
      const cx = ev.type === 'touchend'
        ? (ev.changedTouches?.[0]?.clientX ?? startXRef.current)
        : ev.clientX;
      const delta = cx - startXRef.current;
      const newWidth = Math.max(80, startWidthRef.current + delta);
      updateAttributes({ width: newWidth });
      setResizing(false);
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onEnd);
      document.removeEventListener('touchmove', onMove);
      document.removeEventListener('touchend', onEnd);
    };

    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onEnd);
    document.addEventListener('touchmove', onMove, { passive: false });
    document.addEventListener('touchend', onEnd);
  }, [updateAttributes]);

  const isImage = /\.(png|jpe?g|gif|svg|webp|bmp)$/i.test(filename);

  // Edit mode: resizable full-size image, clickable attachments
  if (editor.isEditable) {
    // Unresolved media: non-clickable placeholder (no URL available)
    if (isUnresolved) {
      return (
        <NodeViewWrapper data-drag-handle className="rte-image-nodeview">
          <div className="rte-image-placeholder" title="Attachment from Jira — open the Attachments panel">
            <span className="rte-image-placeholder-icon">{getFileTypeIcon(filename, 28)}</span>
            <span className="rte-image-placeholder-text">{filename}</span>
            <span className="rte-image-placeholder-hint">Jira-hosted attachment</span>
          </div>
        </NodeViewWrapper>
      );
    }
    // Resolved non-image file or broken image: clickable placeholder for download
    if (imgError || !isImage) {
      return (
        <NodeViewWrapper data-drag-handle className="rte-image-nodeview">
          <div
            className="rte-image-placeholder"
            contentEditable={false}
            style={{ cursor: authSrc ? 'pointer' : 'default' }}
            onMouseDown={(e) => { if (authSrc) { e.preventDefault(); e.stopPropagation(); window.open(authSrc, '_blank'); } }}
            title={authSrc ? `Download ${filename}` : filename}
          >
            <span className="rte-image-placeholder-icon">{getFileTypeIcon(filename, 28)}</span>
            <span className="rte-image-placeholder-text">{filename}</span>
            <MediaDownloadChip authSrc={authSrc} filename={filename} />
          </div>
        </NodeViewWrapper>
      );
    }
    return (
      <NodeViewWrapper data-drag-handle className="rte-image-nodeview">
        <div className={`rte-image-resizable ${selected ? 'selected' : ''} ${resizing ? 'resizing' : ''}`}>
          <MediaDownloadChip authSrc={authSrc} filename={filename} />
          <img
            ref={imgRef}
            src={authSrc}
            alt={alt || ''}
            className="rte-image-full"
            style={width ? { width: `${width}px` } : undefined}
            draggable={false}
            onError={() => setImgError(true)}
          />
          <div
            className="rte-resize-handle rte-resize-handle-right"
            onMouseDown={onResizeStart}
            onTouchStart={onResizeStart}
          >
            <div className="rte-resize-grip" />
          </div>
          <div
            className="rte-resize-handle rte-resize-handle-left"
            onMouseDown={(e) => {
              // For left handle, invert delta
              e.preventDefault();
              e.stopPropagation();
              const clientX = e.type.startsWith('touch') ? e.touches[0].clientX : e.clientX;
              startXRef.current = clientX;
              startWidthRef.current = imgRef.current?.offsetWidth || 300;
              setResizing(true);

              const onMove = (ev) => {
                const cx = ev.type.startsWith('touch') ? ev.touches[0].clientX : ev.clientX;
                const delta = startXRef.current - cx;
                const newWidth = Math.max(80, startWidthRef.current + delta);
                if (imgRef.current) imgRef.current.style.width = `${newWidth}px`;
              };
              const onEnd = (ev) => {
                const cx = ev.type === 'touchend'
                  ? (ev.changedTouches?.[0]?.clientX ?? startXRef.current)
                  : ev.clientX;
                const delta = startXRef.current - cx;
                const newWidth = Math.max(80, startWidthRef.current + delta);
                updateAttributes({ width: newWidth });
                setResizing(false);
                document.removeEventListener('mousemove', onMove);
                document.removeEventListener('mouseup', onEnd);
                document.removeEventListener('touchmove', onMove);
                document.removeEventListener('touchend', onEnd);
              };
              document.addEventListener('mousemove', onMove);
              document.addEventListener('mouseup', onEnd);
              document.addEventListener('touchmove', onMove, { passive: false });
              document.addEventListener('touchend', onEnd);
            }}
            onTouchStart={(e) => {
              e.preventDefault();
              e.stopPropagation();
              const clientX = e.touches[0].clientX;
              startXRef.current = clientX;
              startWidthRef.current = imgRef.current?.offsetWidth || 300;
              setResizing(true);

              const onMove = (ev) => {
                const cx = ev.touches[0].clientX;
                const delta = startXRef.current - cx;
                const newWidth = Math.max(80, startWidthRef.current + delta);
                if (imgRef.current) imgRef.current.style.width = `${newWidth}px`;
              };
              const onEnd = (ev) => {
                const cx = ev.changedTouches?.[0]?.clientX ?? startXRef.current;
                const delta = startXRef.current - cx;
                const newWidth = Math.max(80, startWidthRef.current + delta);
                updateAttributes({ width: newWidth });
                setResizing(false);
                document.removeEventListener('touchmove', onMove);
                document.removeEventListener('touchend', onEnd);
              };
              document.addEventListener('touchmove', onMove, { passive: false });
              document.addEventListener('touchend', onEnd);
            }}
          >
            <div className="rte-resize-grip" />
          </div>
          {resizing && <div className="rte-resize-width-label">{imgRef.current?.offsetWidth || ''}px</div>}
        </div>
      </NodeViewWrapper>
    );
  }

  // Readonly mode
  const formattedDate = formatAttachmentDate(uploadDate);

  // The preview overlay for this one file (read-only only; the editor's
  // own image node is for resizing, not viewing).
  const preview = previewOpen && authSrc ? (
    <AttachmentPreview
      attachments={[{ id: src, filename, created: uploadDate }]}
      index={0}
      onClose={() => setPreviewOpen(false)}
      contentUrl={() => authSrc}
      downloadUrl={() => getDownloadSrc(authSrc) || authSrc}
      renderFileIcon={(name, size) => getFileTypeIcon(name, size)}
      formatSize={() => ''}
      formatWhen={(iso) => formatAttachmentDate(iso)}
    />
  ) : null;
  const openPreview = (e) => {
    e.stopPropagation();
    // Only files the overlay can show open in place; the rest download.
    if (previewKind(filename) === 'none') return;
    handleIssueLinkActivation(e, () => setPreviewOpen(true));
  };

  // Resolved images: render full inline image with download link (restores preview)
  if (isImage && !isUnresolved && !imgError && authSrc) {
    return (
      <NodeViewWrapper className="rte-image-nodeview">
        <span className="rte-image-frame" style={width ? { width: `${width}px` } : undefined}>
          <a href={authSrc} target="_blank" rel="noopener noreferrer" onClick={openPreview}>
            <img
              src={authSrc}
              alt={alt || ''}
              className="rte-image-full"
              style={width ? { width: `${width}px` } : undefined}
              draggable={false}
              onError={() => setImgError(true)}
            />
          </a>
          <MediaDownloadChip authSrc={authSrc} filename={filename} />
        </span>
        {preview}
      </NodeViewWrapper>
    );
  }

  // Non-image files or unresolved: attachment card with file type icon
  const cardContent = (
    <>
      <div className="rte-attachment-thumb">
        {getFileTypeIcon(filename, 48)}
      </div>
      <div className="rte-attachment-info">
        <span className="rte-attachment-name" title={filename}>{filename}</span>
        {formattedDate && (
          <span className="rte-attachment-date">{formattedDate}</span>
        )}
      </div>
    </>
  );

  return (
    <NodeViewWrapper className="rte-attachment-card">
      {authSrc ? (
        <>
          <a href={authSrc} target="_blank" rel="noopener noreferrer" className="rte-attachment-card-link" title={`Open ${filename}`} onClick={openPreview}>
            {cardContent}
          </a>
          <MediaDownloadChip authSrc={authSrc} filename={filename} />
          {preview}
        </>
      ) : (
        <div className="rte-attachment-card-link" title="Attachment from Jira — open the Attachments panel">{cardContent}</div>
      )}
    </NodeViewWrapper>
  );
}

// Extend Image extension with custom attrs + node view
const AttachmentImage = Image.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      uploadDate: { default: null },
      'data-media-id': { default: null },
      width: { default: null },
    };
  },
  addNodeView() {
    return ReactNodeViewRenderer(AttachmentCardView);
  },
});

// Every account id mentioned in a TipTap doc — the chip row hides people
// who are already in the comment.
function mentionedIds(doc) {
  const ids = new Set();
  const walk = (node) => {
    if (!node || typeof node !== 'object') return;
    if (node.type === 'mention' && node.attrs?.id) ids.add(String(node.attrs.id));
    if (Array.isArray(node.content)) node.content.forEach(walk);
  };
  walk(doc);
  return ids;
}

// ─── Suggested mentions: the "+ Name" chips under the comment box ───
// One click puts @Name at the caret, as typing @ and picking would. Names
// only — the row never says why a person is suggested.
function MentionChips({ users, onPick, disabled }) {
  if (!users.length) return null;
  return (
    <div className="rte-mention-chips" aria-label="Suggested people to mention">
      <span className="rte-mention-chips-icon" title="Suggested people to mention" aria-hidden="true">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
          <circle cx="8.5" cy="7" r="4" />
          <line x1="20" y1="8" x2="20" y2="14" />
          <line x1="23" y1="11" x2="17" y2="11" />
        </svg>
      </span>
      {users.map((u) => (
        <button
          key={u.accountId}
          type="button"
          className="rte-mention-chip"
          disabled={disabled}
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => onPick(u)}
          title={`Mention ${u.displayName}`}
        >
          {u.avatarUrls?.['24x24'] ? (
            <img src={api.getJiraImageUrl(u.avatarUrls['24x24'])} alt="" className="rte-mention-chip-avatar" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
          ) : (
            <span className="rte-mention-chip-avatar rte-mention-chip-initial">{(u.displayName || '?')[0]}</span>
          )}
          <span className="rte-mention-chip-plus" aria-hidden="true">+</span>
          <span className="rte-mention-chip-name">{u.displayName}</span>
        </button>
      ))}
    </div>
  );
}

// ─── Mention suggestion list component ───
const MentionList = forwardRef(({ items, command }, ref) => {
  const [selectedIndex, setSelectedIndex] = useState(0);

  const selectItem = useCallback((index) => {
    const item = items[index];
    if (item) {
      command({ id: item.accountId, label: item.displayName });
    }
  }, [items, command]);

  useEffect(() => setSelectedIndex(0), [items]);

  useImperativeHandle(ref, () => ({
    onKeyDown: ({ event }) => {
      if (event.key === 'ArrowUp') {
        setSelectedIndex((prev) => (prev + items.length - 1) % items.length);
        return true;
      }
      if (event.key === 'ArrowDown') {
        setSelectedIndex((prev) => (prev + 1) % items.length);
        return true;
      }
      if (event.key === 'Enter') {
        selectItem(selectedIndex);
        return true;
      }
      return false;
    },
  }));

  if (!items.length) {
    return <div className="mention-list"><div className="mention-list-empty">No users found</div></div>;
  }

  return (
    <div className="mention-list">
      {items.map((item, index) => (
        <button
          key={item.accountId}
          className={`mention-list-item ${index === selectedIndex ? 'selected' : ''}`}
          onClick={() => selectItem(index)}
        >
          {item.avatarUrls?.['24x24'] ? (
            // ATS fork (RE-APPLY-ON-SYNC): a person with no profile photo gets a 404 — show their first letter, not a broken image
            <>
              <img src={api.getJiraImageUrl(item.avatarUrls['24x24'])} alt="" className="mention-list-avatar" onError={(e) => { e.currentTarget.style.display = 'none'; e.currentTarget.nextSibling.style.display = ''; }} />
              <div className="mention-list-avatar-placeholder" style={{ display: 'none' }}>{(item.displayName || '?')[0]}</div>
            </>
          ) : (
            <div className="mention-list-avatar-placeholder">
              {(item.displayName || '?')[0]}
            </div>
          )}
          <span className="mention-list-name">{item.displayName}</span>
        </button>
      ))}
    </div>
  );
});

// Build mention suggestion config. `searchMentions` is the @-mention
// search (active staff only — useMentionSearch), never the user-picker one.
// `suggestedRef.current` is the work item's suggested people (the chip row,
// already ranked by involvement): they lead the @ list — all of them on a
// bare "@", the ones whose name matches once something is typed.
function buildMentionSuggestion(searchMentions, suggestedRef = null) {
  return {
    items: async ({ query }) => {
      const q = String(query || '').toLowerCase();
      const suggested = (suggestedRef?.current || [])
        .filter(u => !q || String(u.displayName || '').toLowerCase().includes(q));
      if (!searchMentions) return suggested.slice(0, 8);
      try {
        const users = await searchMentions(query);
        const seen = new Set(suggested.map(u => u.accountId));
        const rest = (users || []).filter(u => u.accountType !== 'app' && !seen.has(u.accountId));
        return [...suggested, ...rest].slice(0, 8);
      } catch {
        return suggested.slice(0, 8);
      }
    },
    render: () => {
      let component;
      let popup;

      return {
        onStart: (props) => {
          component = new ReactRenderer(MentionList, {
            props,
            editor: props.editor,
          });

          if (!props.clientRect) return;

          popup = tippy('body', {
            getReferenceClientRect: props.clientRect,
            appendTo: () => document.body,
            content: component.element,
            showOnCreate: true,
            interactive: true,
            trigger: 'manual',
            placement: 'bottom-start',
            theme: 'mention',
          });
        },
        onUpdate: (props) => {
          component?.updateProps(props);
          if (popup?.[0] && props.clientRect) {
            popup[0].setProps({ getReferenceClientRect: props.clientRect });
          }
        },
        onKeyDown: (props) => {
          if (props.event.key === 'Escape') {
            popup?.[0]?.hide();
            return true;
          }
          return component?.ref?.onKeyDown(props) || false;
        },
        onExit: () => {
          popup?.[0]?.destroy();
          component?.destroy();
        },
      };
    },
  };
}

// Parse HTML table (from Excel paste) and insert as TipTap table
function insertTableFromHtml(editor, html) {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  const table = doc.querySelector('table');
  if (!table) return;

  const rows = Array.from(table.querySelectorAll('tr'));
  if (rows.length === 0) return;

  // Build TipTap table JSON
  const tableContent = rows.map((tr, rowIndex) => {
    const cells = Array.from(tr.querySelectorAll('td, th'));
    return {
      type: 'tableRow',
      content: cells.map((cell) => {
        const isHeader = cell.tagName === 'TH' || rowIndex === 0;
        const text = cell.textContent || '';
        const attrs = {};

        // Preserve colspan/rowspan
        const colspan = parseInt(cell.getAttribute('colspan'));
        const rowspan = parseInt(cell.getAttribute('rowspan'));
        if (colspan > 1) attrs.colspan = colspan;
        if (rowspan > 1) attrs.rowspan = rowspan;

        // Preserve background color from Excel
        const bg = cell.style?.backgroundColor;
        if (bg && bg !== 'transparent' && bg !== '') {
          attrs.background = bg;
        }

        return {
          type: isHeader ? 'tableHeader' : 'tableCell',
          attrs: Object.keys(attrs).length ? attrs : undefined,
          content: [{ type: 'paragraph', content: text ? [{ type: 'text', text }] : [] }],
        };
      }),
    };
  });

  editor.chain().focus().insertContent({ type: 'table', content: tableContent }).run();
}

// `autoFocus` puts the caret at the end of the document as soon as the editor
// mounts. Opt-in (default off) so only editors the user deliberately opened —
// the comment box, a reply box — steal focus.
// `mentionSuggestions` (optional) — the work item's suggested people, from
// useMentionSuggestions: drawn as the chip row under the editor and leading
// the @ list. Only the comment box passes it.
function RichTextEditor({ value, onChange, disabled, readOnly, onFileUpload, onBlur, autoFocus = false, mentionSuggestions = null }) {
  const [linkPopupOpen, setLinkPopupOpen] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const [uploading, setUploading] = useState(null); // { filename } or null
  const [uploadError, setUploadError] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [tableMenuOpen, setTableMenuOpen] = useState(false);
  const [cameraOpen, setCameraOpen] = useState(false);
  const fileInputRef = useRef(null);
  const cameraVideoRef = useRef(null);
  const cameraStreamRef = useRef(null);
  const dragCounterRef = useRef(0);
  const wrapperRef = useRef(null);
  const tableMenuRef = useRef(null);

  // The editor owns its mention search, so no caller can hand it the
  // user-picker search (which offers inactive accounts).
  const searchMentions = useMentionSearch();
  const suggestedRef = useRef(mentionSuggestions || []);
  suggestedRef.current = mentionSuggestions || [];
  const mentionSuggestion = useRef(buildMentionSuggestion(searchMentions, suggestedRef)).current;
  // Who the comment already mentions — kept current from onUpdate.
  const [inDoc, setInDoc] = useState(() => mentionedIds(value));

  // TipTap v3 returns the editor instance from useEditor before its
  // ProseMirror view is mounted. On iOS Safari that delay is enough that
  // any useEffect that reads `editor.view.dom` immediately fires too
  // early — TipTap's defensive getter throws "The editor view is not
  // available", crashing the entire mobile layout. Gate those effects on
  // this flag, flipped by the onCreate lifecycle hook below.
  const [editorReady, setEditorReady] = useState(false);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
        // TipTap v3 bundles Link + Underline in StarterKit. We register
        // them explicitly below with custom config (openOnClick: false,
        // autolink), so disable the bundled versions here to avoid the
        // "Duplicate extension names" warning that prevents the editor
        // view from mounting on mobile Safari.
        link: false,
        underline: false,
      }),
      Link.configure({
        openOnClick: false,
        autolink: true,
      }),
      Underline,
      AttachmentImage.configure({
        inline: false,
        allowBase64: false,
      }),
      Mention.configure({
        HTMLAttributes: { class: 'rte-mention' },
        suggestion: mentionSuggestion,
      }),
      Table.configure({
        resizable: true,
        HTMLAttributes: { class: 'rte-table' },
      }),
      TableRow,
      TableHeader,
      TableCell,
      TableDeleteKeymap,
      TextColor,
      SmartLink,
    ],
    content: value || { type: 'doc', content: [{ type: 'paragraph', content: [] }] },
    editable: !readOnly && !disabled,
    autofocus: autoFocus && !readOnly && !disabled ? 'end' : false,
    onCreate: () => setEditorReady(true),
    onDestroy: () => setEditorReady(false),
    onUpdate: ({ editor }) => {
      const json = editor.getJSON();
      if (onChange) {
        onChange(json);
      }
      setInDoc(mentionedIds(json));
    },
    onBlur: () => {
      if (onBlur) onBlur();
    },
  });

  // Keep editable state in sync — useEditor only applies it on mount
  useEffect(() => {
    if (editor) {
      editor.setEditable(!readOnly && !disabled);
    }
  }, [editor, readOnly, disabled]);

  // Floating table controls — + buttons on right edge (add column) and bottom edge (add row)
  useEffect(() => {
    if (!editor || !editorReady || readOnly || disabled) return;
    const editorEl = editor.view.dom;
    if (!editorEl) return;

    let addColBtn = null;
    let addRowBtn = null;
    let currentTable = null;

    const createBtn = (className, label) => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = className;
      btn.innerHTML = '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>';
      btn.title = label;
      return btn;
    };

    const showControls = (table) => {
      if (currentTable === table) return;
      hideControls();
      currentTable = table;
      const wrapper = table.closest('.tableWrapper') || table.parentElement;
      if (!wrapper) return;
      wrapper.style.position = 'relative';

      addColBtn = createBtn('rte-table-add-col-btn', 'Add column');
      addRowBtn = createBtn('rte-table-add-row-btn', 'Add row');

      addColBtn.addEventListener('mousedown', (e) => {
        e.preventDefault();
        e.stopPropagation();
        editor.chain().focus().addColumnAfter().run();
      });
      addRowBtn.addEventListener('mousedown', (e) => {
        e.preventDefault();
        e.stopPropagation();
        editor.chain().focus().addRowAfter().run();
      });

      wrapper.appendChild(addColBtn);
      wrapper.appendChild(addRowBtn);
    };

    const hideControls = () => {
      if (addColBtn) { addColBtn.remove(); addColBtn = null; }
      if (addRowBtn) { addRowBtn.remove(); addRowBtn = null; }
      currentTable = null;
    };

    const handleMouseOver = (e) => {
      const table = e.target.closest('.rte-table') || e.target.closest('table');
      if (table) showControls(table);
    };

    const handleMouseLeave = (e) => {
      const related = e.relatedTarget;
      if (related?.closest?.('.rte-table-add-col-btn') || related?.closest?.('.rte-table-add-row-btn')) return;
      if (related?.closest?.('.rte-table') === currentTable) return;
      hideControls();
    };

    editorEl.addEventListener('mouseover', handleMouseOver);
    editorEl.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      editorEl.removeEventListener('mouseover', handleMouseOver);
      editorEl.removeEventListener('mouseleave', handleMouseLeave);
      hideControls();
    };
  }, [editor, editorReady, readOnly, disabled]);

  // Process a file: upload and insert into editor
  const processFile = useCallback(async (file) => {
    if (!editor || !onFileUpload) return;

    setUploadError(null);
    setUploading({ filename: file.name });

    try {
      const attachment = await onFileUpload(file);
      if (!attachment) throw new Error('Upload returned no data');

      const contentUrl = attachment.content;

      // Always insert as an image node — the AttachmentCardView in this
      // editor renders non-images as a clickable file-type tile (PDF,
      // DOC, etc.) and handles authentication via getAuthenticatedSrc
      // appending ?token=. The previous link-mark fallback for non-
      // images opened the proxy URL in a new tab without a token,
      // producing a 401 ("No token provided"), and serialised to a
      // plain <a> in ADF instead of a proper media[type=file] tile in
      // Jira. After the file that went in before it, not over it — see
      // attachmentInsertRange.
      editor.chain().focus().insertContentAt(attachmentInsertRange(editor.state.selection), {
        type: 'image',
        attrs: {
          src: contentUrl,
          alt: attachment.filename,
          uploadDate: attachment.created || new Date().toISOString(),
          'data-media-id': attachment.mediaApiFileId || null,
        },
      }).run();
    } catch (err) {
      const msg = err.response?.data?.details?.errors
        ? Object.values(err.response.data.details.errors).join(', ')
        : err.response?.data?.error || err.message || 'Upload failed';
      setUploadError(msg);
      setTimeout(() => setUploadError(null), 5000);
    } finally {
      setUploading(null);
    }
  }, [editor, onFileUpload]);

  // Process multiple files sequentially
  const processFiles = useCallback(async (files) => {
    for (const file of files) {
      await processFile(file);
    }
  }, [processFile]);

  // Drag & drop on the whole editor wrapper (so overlay doesn't break events)
  const handleDragEnter = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current++;
    if (e.dataTransfer?.types?.includes('Files')) {
      setDragActive(true);
    }
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current--;
    if (dragCounterRef.current === 0) {
      setDragActive(false);
    }
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current = 0;
    setDragActive(false);
    const files = e.dataTransfer?.files;
    if (files && files.length > 0) {
      processFiles(Array.from(files));
    }
  }, [processFiles]);

  // Paste handler for files (screenshots, etc.) and Excel tables
  useEffect(() => {
    if (!editor || !editorReady || readOnly) return;

    const editorDom = editor.view.dom;

    const handlePaste = (e) => {
      const clipboardData = e.clipboardData;
      if (!clipboardData) return;

      // Check for HTML content first (Excel copies tables as HTML)
      const html = clipboardData.getData('text/html');
      if (html && html.includes('<table')) {
        e.preventDefault();
        e.stopPropagation();
        insertTableFromHtml(editor, html);
        return;
      }

      // Then check for files
      if (!onFileUpload) return;
      const items = clipboardData.items;
      if (!items) return;

      const files = [];
      for (const item of items) {
        if (item.kind === 'file') {
          const file = item.getAsFile();
          if (file) files.push(file);
        }
      }

      if (files.length > 0) {
        e.preventDefault();
        e.stopPropagation();
        processFiles(files);
      }
    };

    editorDom.addEventListener('paste', handlePaste);
    return () => editorDom.removeEventListener('paste', handlePaste);
  }, [editor, editorReady, readOnly, onFileUpload, processFiles]);

  // Close table menu on outside click
  useEffect(() => {
    if (!tableMenuOpen) return;
    const handleClick = (e) => {
      if (tableMenuRef.current && !tableMenuRef.current.contains(e.target)) {
        setTableMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [tableMenuOpen]);

  const handleLinkSubmit = useCallback(() => {
    if (!editor || !linkUrl) {
      setLinkPopupOpen(false);
      setLinkUrl('');
      return;
    }

    const url = linkUrl.startsWith('http') ? linkUrl : `https://${linkUrl}`;
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
    setLinkPopupOpen(false);
    setLinkUrl('');
  }, [editor, linkUrl]);

  const handleLinkKeyDown = useCallback((e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleLinkSubmit();
    }
    if (e.key === 'Escape') {
      setLinkPopupOpen(false);
      setLinkUrl('');
    }
  }, [handleLinkSubmit]);

  const openLinkPopup = useCallback(() => {
    if (!editor) return;
    const existing = editor.getAttributes('link').href || '';
    setLinkUrl(existing);
    setLinkPopupOpen(true);
  }, [editor]);

  const handleFileButtonClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const openCamera = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment', width: { ideal: 1920 }, height: { ideal: 1080 } },
        audio: false,
      });
      cameraStreamRef.current = stream;
      setCameraOpen(true);
      // Attach stream to video element after render
      requestAnimationFrame(() => {
        if (cameraVideoRef.current) {
          cameraVideoRef.current.srcObject = stream;
        }
      });
    } catch (err) {
      setUploadError('Could not access camera: ' + (err.message || 'Permission denied'));
      setTimeout(() => setUploadError(null), 5000);
    }
  }, []);

  const closeCamera = useCallback(() => {
    if (cameraStreamRef.current) {
      cameraStreamRef.current.getTracks().forEach(t => t.stop());
      cameraStreamRef.current = null;
    }
    setCameraOpen(false);
  }, []);

  const capturePhoto = useCallback(() => {
    const video = cameraVideoRef.current;
    if (!video) return;
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext('2d').drawImage(video, 0, 0);
    canvas.toBlob((blob) => {
      if (blob) {
        const file = new File([blob], `photo-${Date.now()}.jpg`, { type: 'image/jpeg' });
        closeCamera();
        processFile(file);
      }
    }, 'image/jpeg', 0.92);
  }, [closeCamera, processFile]);

  const handleFileInputChange = useCallback((e) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      processFiles(Array.from(files));
    }
    // Reset so the same file can be selected again
    e.target.value = '';
  }, [processFiles]);

  if (!editor) return null;

  if (readOnly) {
    return (
      <div className="rich-text-editor readonly">
        <EditorContent editor={editor} />
      </div>
    );
  }

  const canDrop = !readOnly && !disabled && !!onFileUpload;

  const chipUsers = (mentionSuggestions || []).filter(u => !inDoc.has(String(u.accountId)));
  const pickSuggested = (u) => {
    editor.chain().focus().insertContent([
      { type: 'mention', attrs: { id: u.accountId, label: u.displayName } },
      { type: 'text', text: ' ' },
    ]).run();
  };

  return (
    <div
      className={`rich-text-editor${chipUsers.length ? ' has-mention-chips' : ''}`}
      ref={wrapperRef}
      onDragEnter={canDrop ? handleDragEnter : undefined}
      onDragLeave={canDrop ? handleDragLeave : undefined}
      onDragOver={canDrop ? handleDragOver : undefined}
      onDrop={canDrop ? handleDrop : undefined}
    >
      {/* Toolbar */}
      <div className="rte-toolbar">
        {/* Group 1: Inline formatting */}
        <div className="rte-toolbar-group">
          <button type="button" className={`rte-toolbar-btn ${editor.isActive('bold') ? 'active' : ''}`} onMouseDown={(e) => e.preventDefault()} onClick={() => editor.chain().focus().toggleBold().run()} disabled={disabled} title="Bold">
            <strong>B</strong>
          </button>
          <button type="button" className={`rte-toolbar-btn ${editor.isActive('italic') ? 'active' : ''}`} onMouseDown={(e) => e.preventDefault()} onClick={() => editor.chain().focus().toggleItalic().run()} disabled={disabled} title="Italic">
            <em>I</em>
          </button>
          <button type="button" className={`rte-toolbar-btn ${editor.isActive('underline') ? 'active' : ''}`} onMouseDown={(e) => e.preventDefault()} onClick={() => editor.chain().focus().toggleUnderline().run()} disabled={disabled} title="Underline">
            <span style={{ textDecoration: 'underline' }}>U</span>
          </button>
          <button type="button" className={`rte-toolbar-btn ${editor.isActive('strike') ? 'active' : ''}`} onMouseDown={(e) => e.preventDefault()} onClick={() => editor.chain().focus().toggleStrike().run()} disabled={disabled} title="Strikethrough">
            <span style={{ textDecoration: 'line-through' }}>S</span>
          </button>
          <button type="button" className={`rte-toolbar-btn ${editor.isActive('code') ? 'active' : ''}`} onMouseDown={(e) => e.preventDefault()} onClick={() => editor.chain().focus().toggleCode().run()} disabled={disabled} title="Code">
            {'<>'}
          </button>
        </div>

        {/* Group 2: Headings */}
        <div className="rte-toolbar-group">
          <button type="button" className={`rte-toolbar-btn ${editor.isActive('heading', { level: 1 }) ? 'active' : ''}`} onMouseDown={(e) => e.preventDefault()} onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} disabled={disabled} title="Heading 1">
            H1
          </button>
          <button type="button" className={`rte-toolbar-btn ${editor.isActive('heading', { level: 2 }) ? 'active' : ''}`} onMouseDown={(e) => e.preventDefault()} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} disabled={disabled} title="Heading 2">
            H2
          </button>
          <button type="button" className={`rte-toolbar-btn ${editor.isActive('heading', { level: 3 }) ? 'active' : ''}`} onMouseDown={(e) => e.preventDefault()} onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} disabled={disabled} title="Heading 3">
            H3
          </button>
        </div>

        {/* Group 3: Lists */}
        <div className="rte-toolbar-group">
          <button type="button" className={`rte-toolbar-btn ${editor.isActive('bulletList') ? 'active' : ''}`} onMouseDown={(e) => e.preventDefault()} onClick={() => editor.chain().focus().toggleBulletList().run()} disabled={disabled} title="Bullet list">
            &#8226;
          </button>
          <button type="button" className={`rte-toolbar-btn ${editor.isActive('orderedList') ? 'active' : ''}`} onMouseDown={(e) => e.preventDefault()} onClick={() => editor.chain().focus().toggleOrderedList().run()} disabled={disabled} title="Ordered list">
            1.
          </button>
        </div>

        {/* Group 4: Block elements */}
        <div className="rte-toolbar-group">
          <button type="button" className={`rte-toolbar-btn ${editor.isActive('blockquote') ? 'active' : ''}`} onMouseDown={(e) => e.preventDefault()} onClick={() => editor.chain().focus().toggleBlockquote().run()} disabled={disabled} title="Blockquote">
            &#8220;
          </button>
          <button type="button" className={`rte-toolbar-btn ${editor.isActive('codeBlock') ? 'active' : ''}`} onMouseDown={(e) => e.preventDefault()} onClick={() => editor.chain().focus().toggleCodeBlock().run()} disabled={disabled} title="Code block">
            {'{ }'}
          </button>
          <button type="button" className="rte-toolbar-btn" onMouseDown={(e) => e.preventDefault()} onClick={() => editor.chain().focus().setHorizontalRule().run()} disabled={disabled} title="Horizontal rule">
            &#8212;
          </button>
        </div>

        {/* Group 5: Table */}
        <div className="rte-toolbar-group rte-table-menu-wrap" ref={tableMenuRef}>
          <button
            type="button"
            className={`rte-toolbar-btn ${editor.isActive('table') ? 'active' : ''}`}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => setTableMenuOpen(!tableMenuOpen)}
            disabled={disabled}
            title="Table"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="3" width="18" height="18" rx="2" />
              <line x1="3" y1="9" x2="21" y2="9" />
              <line x1="3" y1="15" x2="21" y2="15" />
              <line x1="9" y1="3" x2="9" y2="21" />
              <line x1="15" y1="3" x2="15" y2="21" />
            </svg>
          </button>
          {tableMenuOpen && (
            <div className="rte-table-dropdown">
              {!editor.isActive('table') ? (
                <button className="rte-table-dropdown-item" onMouseDown={(e) => e.preventDefault()} onClick={() => { editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run(); setTableMenuOpen(false); }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2" /><line x1="3" y1="9" x2="21" y2="9" /><line x1="9" y1="3" x2="9" y2="21" /></svg>
                  Insert table
                </button>
              ) : (
                <>
                  <button className="rte-table-dropdown-item" onMouseDown={(e) => e.preventDefault()} onClick={() => { editor.chain().focus().addColumnBefore().run(); setTableMenuOpen(false); }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
                    Add column before
                  </button>
                  <button className="rte-table-dropdown-item" onMouseDown={(e) => e.preventDefault()} onClick={() => { editor.chain().focus().addColumnAfter().run(); setTableMenuOpen(false); }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
                    Add column after
                  </button>
                  <button className="rte-table-dropdown-item" onMouseDown={(e) => e.preventDefault()} onClick={() => { editor.chain().focus().addRowBefore().run(); setTableMenuOpen(false); }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
                    Add row above
                  </button>
                  <button className="rte-table-dropdown-item" onMouseDown={(e) => e.preventDefault()} onClick={() => { editor.chain().focus().addRowAfter().run(); setTableMenuOpen(false); }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
                    Add row below
                  </button>
                  <div className="rte-table-dropdown-sep" />
                  <button className="rte-table-dropdown-item" onMouseDown={(e) => e.preventDefault()} onClick={() => { editor.chain().focus().deleteColumn().run(); setTableMenuOpen(false); }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                    Delete column
                  </button>
                  <button className="rte-table-dropdown-item" onMouseDown={(e) => e.preventDefault()} onClick={() => { editor.chain().focus().deleteRow().run(); setTableMenuOpen(false); }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                    Delete row
                  </button>
                  <button className="rte-table-dropdown-item rte-table-dropdown-danger" onMouseDown={(e) => e.preventDefault()} onClick={() => { editor.chain().focus().deleteTable().run(); setTableMenuOpen(false); }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /></svg>
                    Delete table
                  </button>
                  <div className="rte-table-dropdown-sep" />
                  <button className="rte-table-dropdown-item" onMouseDown={(e) => e.preventDefault()} onClick={() => { editor.chain().focus().toggleHeaderRow().run(); setTableMenuOpen(false); }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2" /><line x1="3" y1="9" x2="21" y2="9" /></svg>
                    Toggle header row
                  </button>
                  <button className="rte-table-dropdown-item" onMouseDown={(e) => e.preventDefault()} onClick={() => { editor.chain().focus().mergeCells().run(); setTableMenuOpen(false); }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2" /><line x1="12" y1="3" x2="12" y2="21" /></svg>
                    Merge cells
                  </button>
                  <button className="rte-table-dropdown-item" onMouseDown={(e) => e.preventDefault()} onClick={() => { editor.chain().focus().splitCell().run(); setTableMenuOpen(false); }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2" /><line x1="12" y1="3" x2="12" y2="21" /><line x1="3" y1="12" x2="21" y2="12" /></svg>
                    Split cell
                  </button>
                </>
              )}
            </div>
          )}
        </div>

        {/* Group 6: Link */}
        <div className="rte-toolbar-group">
          {linkPopupOpen ? (
            <div className="rte-link-popup">
              <input
                className="rte-link-input"
                type="text"
                placeholder="Enter URL..."
                value={linkUrl}
                onChange={(e) => setLinkUrl(e.target.value)}
                onKeyDown={handleLinkKeyDown}
                autoFocus
              />
              <button type="button" className="rte-link-confirm" onClick={handleLinkSubmit}>&#10003;</button>
              <button type="button" className="rte-link-cancel" onClick={() => { setLinkPopupOpen(false); setLinkUrl(''); }}>&#10005;</button>
            </div>
          ) : (
            <button type="button" className={`rte-toolbar-btn ${editor.isActive('link') ? 'active' : ''}`} onMouseDown={(e) => e.preventDefault()} onClick={openLinkPopup} disabled={disabled} title="Link">
              &#128279;
            </button>
          )}
        </div>

        {/* Group 6: Undo/Redo */}
        <div className="rte-toolbar-group">
          <button type="button" className="rte-toolbar-btn" onMouseDown={(e) => e.preventDefault()} onClick={() => editor.chain().focus().undo().run()} disabled={disabled || !editor.can().undo()} title="Undo">
            &#8617;
          </button>
          <button type="button" className="rte-toolbar-btn" onMouseDown={(e) => e.preventDefault()} onClick={() => editor.chain().focus().redo().run()} disabled={disabled || !editor.can().redo()} title="Redo">
            &#8618;
          </button>
        </div>

        {/* Group 7: Attachment */}
        {onFileUpload && (
          <div className="rte-toolbar-group">
            <button type="button" className="rte-toolbar-btn" onMouseDown={(e) => e.preventDefault()} onClick={handleFileButtonClick} disabled={disabled || !!uploading} title="Attach file">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
              </svg>
            </button>
            <button type="button" className="rte-toolbar-btn" onMouseDown={(e) => e.preventDefault()} onClick={openCamera} disabled={disabled || !!uploading} title="Take photo">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
                <circle cx="12" cy="13" r="4" />
              </svg>
            </button>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              style={{ display: 'none' }}
              onChange={handleFileInputChange}
            />
          </div>
        )}
      </div>

      {/* Camera modal */}
      {cameraOpen && (
        <div className="rte-camera-overlay" onClick={closeCamera}>
          <div className="rte-camera-modal" onClick={e => e.stopPropagation()}>
            <video ref={cameraVideoRef} autoPlay playsInline muted className="rte-camera-video" />
            <div className="rte-camera-controls">
              <button type="button" className="rte-camera-cancel" onClick={closeCamera}>Cancel</button>
              <button type="button" className="rte-camera-capture" onClick={capturePhoto}>
                <span className="rte-camera-shutter" />
              </button>
              <span className="rte-camera-spacer" />
            </div>
          </div>
        </div>
      )}

      {/* Upload indicator */}
      {uploading && (
        <div className="rte-upload-indicator">
          <div className="rte-upload-indicator-content">
            <span className="rte-upload-spinner" />
            <span className="rte-upload-text">Uploading <strong>{uploading.filename}</strong></span>
          </div>
          <div className="rte-upload-progress-bar" />
        </div>
      )}

      {/* Upload error */}
      {uploadError && (
        <div className="rte-upload-error">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" />
            <line x1="15" y1="9" x2="9" y2="15" />
            <line x1="9" y1="9" x2="15" y2="15" />
          </svg>
          {uploadError}
        </div>
      )}

      {/* Editor content area wrapper */}
      <div className="rte-editor-area">
        <EditorContent editor={editor} />

        {/* Drag & drop overlay */}
        {dragActive && (
          <div className="rte-drop-overlay">
            <div className="rte-drop-overlay-content">
              <div className="rte-drop-icon">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="17 8 12 3 7 8" />
                  <line x1="12" y1="3" x2="12" y2="15" />
                </svg>
              </div>
              <span className="rte-drop-label">Drop files to attach</span>
              <span className="rte-drop-hint">Images will be embedded inline</span>
            </div>
          </div>
        )}
      </div>

      <MentionChips users={chipUsers} onPick={pickSuggested} disabled={disabled} />
    </div>
  );
}

export default RichTextEditor;
